import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Search, Plus, Edit3, Trash2, User, Mail, Phone, Tag as TagIcon, X, BookUser, Calendar } from "lucide-react";

/**
 * ContactsPanel — Rubrica clienti per-business (CRUD completo).
 * Indipendente dai clienti registrati: salva nome/email/telefono/note/tag/data nascita
 * per clienti walk-in che non hanno (e non vogliono) un account.
 */
export default function ContactsPanel({ businessId }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // contact or { new: true }
  const [toast, setToast] = useState("");

  function showToast(m) { setToast(m); setTimeout(() => setToast(""), 2500); }

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/businesses/${businessId}/contacts`, { params: q ? { q } : {} });
      setContacts(data || []);
    } catch {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, q]);

  async function remove(c) {
    if (!window.confirm(`Eliminare ${c.name} dalla rubrica? Lo storico prenotazioni resta intatto.`)) return;
    try {
      await api.delete(`/contacts/${c.id}`);
      showToast("Contatto eliminato");
      load();
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Errore");
    }
  }

  const stats = useMemo(() => {
    const withEmail = contacts.filter((c) => c.email).length;
    const withPhone = contacts.filter((c) => c.phone).length;
    const linked = contacts.filter((c) => c.linked_customer_id).length;
    return { total: contacts.length, withEmail, withPhone, linked };
  }, [contacts]);

  return (
    <div className="space-y-5" data-testid="contacts-panel">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600 inline-flex items-center gap-2">
            <BookUser className="h-3.5 w-3.5" /> Rubrica clienti
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight mt-1">Contatti</h2>
          <p className="text-sm text-zinc-500 max-w-2xl mt-1 dark:text-zinc-400">
            Salva nome, email e telefono dei clienti walk-in senza obbligarli a registrarsi. Riusabili in prenotazione manuale e nei clienti abituali.
          </p>
        </div>
        <button
          onClick={() => setEditing({ new: true })}
          className="h-10 px-4 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium inline-flex items-center gap-2 self-start"
          data-testid="contacts-add"
        >
          <Plus className="h-4 w-4" /> Nuovo contatto
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Totali" value={stats.total} accent="blue" />
        <Stat label="Con email" value={stats.withEmail} accent="emerald" />
        <Stat label="Con telefono" value={stats.withPhone} accent="violet" />
        <Stat label="Collegati ad account" value={stats.linked} accent="amber" />
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 dark:text-zinc-500" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca per nome, email, telefono o tag…"
          className="input-soft pl-10"
          data-testid="contacts-search"
        />
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 py-8 dark:text-zinc-400">Caricamento…</div>
      ) : contacts.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-zinc-300 p-10 text-center dark:border-zinc-600">
          <BookUser className="h-10 w-10 mx-auto text-zinc-300" />
          <div className="mt-3 font-medium">{q ? "Nessun contatto trovato" : "Nessun contatto in rubrica"}</div>
          <div className="text-sm text-zinc-500 mt-1 dark:text-zinc-400">
            {q ? "Prova a modificare la ricerca." : "Aggiungi il primo cliente walk-in con il pulsante \"Nuovo contatto\"."}
          </div>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {contacts.map((c) => (
            <ContactCard
              key={c.id}
              contact={c}
              onEdit={() => setEditing(c)}
              onDelete={() => remove(c)}
            />
          ))}
        </div>
      )}

      {editing && (
        <ContactEditor
          businessId={businessId}
          contact={editing.new ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); showToast("Contatto salvato"); }}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-zinc-900 text-white text-sm px-4 py-2 rounded-full shadow-lg z-[70]" data-testid="contacts-toast">{toast}</div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  const ring = {
    blue: "from-blue-500/10 to-blue-500/0 ring-blue-500/20",
    emerald: "from-emerald-500/10 to-emerald-500/0 ring-emerald-500/20",
    violet: "from-violet-500/10 to-violet-500/0 ring-violet-500/20",
    amber: "from-amber-500/10 to-amber-500/0 ring-amber-500/20",
  }[accent];
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${ring} ring-1 p-4`}>
      <div className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">{label}</div>
      <div className="font-display text-2xl mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function ContactCard({ contact, onEdit, onDelete }) {
  return (
    <div className="rounded-2xl bg-white ring-1 ring-zinc-100 p-4 dark:bg-zinc-900 dark:ring-zinc-800" data-testid={`contact-card-${contact.id}`}>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-medium flex-shrink-0 dark:bg-blue-950/40">
          {(contact.name || "?").trim()[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{contact.name}</div>
          <div className="mt-1 text-xs text-zinc-500 space-y-0.5 dark:text-zinc-400">
            {contact.email && (
              <div className="inline-flex items-center gap-1.5 truncate"><Mail className="h-3 w-3 flex-shrink-0" /> <span className="truncate">{contact.email}</span></div>
            )}
            {contact.email && contact.phone && <span className="mx-1.5 hidden md:inline">·</span>}
            {contact.phone && (
              <div className="inline-flex items-center gap-1.5"><Phone className="h-3 w-3" /> {contact.phone}</div>
            )}
            {contact.date_of_birth && (
              <div className="inline-flex items-center gap-1.5"><Calendar className="h-3 w-3" /> {fmtDate(contact.date_of_birth)}</div>
            )}
          </div>
          {contact.tags && contact.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {contact.tags.map((t) => (
                <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 inline-flex items-center gap-0.5 dark:bg-blue-950/40 dark:text-blue-300">
                  <TagIcon className="h-2.5 w-2.5" />{t}
                </span>
              ))}
            </div>
          )}
          {contact.notes && (
            <div className="mt-2 text-xs text-zinc-600 line-clamp-2 whitespace-pre-line dark:text-zinc-400">{contact.notes}</div>
          )}
          {contact.linked_customer_id && (
            <div className="mt-2 text-[10px] inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full dark:text-emerald-300 dark:bg-emerald-950/40">
              <User className="h-2.5 w-2.5" /> Account collegato
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <button onClick={onEdit} className="h-8 w-8 rounded-full hover:bg-zinc-100 inline-flex items-center justify-center text-zinc-600 dark:hover:bg-zinc-800 dark:text-zinc-400" title="Modifica" data-testid={`contact-edit-${contact.id}`}>
            <Edit3 className="h-4 w-4" />
          </button>
          <button onClick={onDelete} className="h-8 w-8 rounded-full hover:bg-rose-50 hover:text-rose-600 inline-flex items-center justify-center text-zinc-500 dark:text-zinc-400 dark:bg-rose-950/40" title="Elimina" data-testid={`contact-delete-${contact.id}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ContactEditor({ businessId, contact, onClose, onSaved }) {
  const isNew = !contact;
  const [form, setForm] = useState({
    name: contact?.name || "",
    email: contact?.email || "",
    phone: contact?.phone || "",
    date_of_birth: contact?.date_of_birth || "",
    notes: contact?.notes || "",
    tags: (contact?.tags || []).join(", "),
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e?.preventDefault();
    setErr("");
    if (!form.name || form.name.trim().length < 2) {
      setErr("Inserisci nome e cognome (min 2 caratteri)");
      return;
    }
    setBusy(true);
    try {
      const payload = { name: form.name.trim() };
      if (form.email) payload.email = form.email.trim().toLowerCase();
      if (form.phone) payload.phone = form.phone.trim();
      if (form.date_of_birth) payload.date_of_birth = form.date_of_birth;
      if (form.notes) payload.notes = form.notes.trim();
      const tags = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
      payload.tags = tags;
      if (isNew) {
        await api.post(`/businesses/${businessId}/contacts`, payload);
      } else {
        await api.put(`/contacts/${contact.id}`, payload);
      }
      onSaved();
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Errore salvataggio");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end md:items-center justify-center p-4" onClick={onClose} data-testid="contact-editor">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md p-6 card-soft max-h-[92vh] overflow-y-auto dark:bg-zinc-900">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">{isNew ? "Nuovo contatto" : "Modifica contatto"}</div>
            <h2 className="font-display text-2xl font-medium mt-1">{isNew ? "Aggiungi cliente" : contact.name}</h2>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full hover:bg-zinc-100 inline-flex items-center justify-center dark:hover:bg-zinc-800" data-testid="contact-editor-close"><X className="h-4 w-4" /></button>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-3">
          <Field label="Nome e cognome *">
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input-soft"
              placeholder="Mario Rossi"
              data-testid="contact-editor-name"
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Email">
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input-soft"
                placeholder="mario@example.com"
                data-testid="contact-editor-email"
              />
            </Field>
            <Field label="Telefono">
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="input-soft"
                placeholder="+39 333 1234567"
                data-testid="contact-editor-phone"
              />
            </Field>
          </div>

          <Field label="Data di nascita (facoltativa)">
            <input
              type="date"
              value={form.date_of_birth}
              onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
              className="input-soft"
              data-testid="contact-editor-dob"
            />
          </Field>

          <Field label="Tag (separati da virgola)">
            <input
              type="text"
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              className="input-soft"
              placeholder="VIP, Sensibile, Sconto fedeltà"
              data-testid="contact-editor-tags"
            />
          </Field>

          <Field label="Note interne">
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="input-soft h-auto py-3"
              placeholder="Es: preferisce orari pomeridiani"
              data-testid="contact-editor-notes"
            />
            <p className="text-[10px] text-zinc-400 mt-1 dark:text-zinc-500">Non includere dati sanitari sensibili (DPA art. 9).</p>
          </Field>

          {err && <div className="text-sm text-red-600" data-testid="contact-editor-error">{err}</div>}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 btn-secondary h-11 text-sm">Annulla</button>
            <button type="submit" disabled={busy} className="flex-1 btn-accent h-11 text-sm" data-testid="contact-editor-submit">
              {busy ? "Salvataggio…" : isNew ? "Crea contatto" : "Salva"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function fmtDate(iso) {
  try {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  } catch {
    return iso;
  }
}
